import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ReportRange, ReportRangeDays } from '../types/reports.types';
import { daysAgoDate, fmtShortDate, isoDate } from '../utils/report-format';

const PRESETS: ReportRangeDays[] = [7, 30, 90];
const PRESET_LABEL: Record<number, string> = {
  7: 'Last 7 days',
  30: 'Last 30 days',
  90: 'Last 90 days',
};

function rangeForPreset(days: number): ReportRange {
  return {
    days,
    from: isoDate(daysAgoDate(days)),
    to: isoDate(new Date()),
    label: PRESET_LABEL[days] || `Last ${days} days`,
    isCustom: false,
  };
}

function parseDays(raw: string | null): ReportRangeDays | null {
  const n = Number.parseInt(raw || '', 10);
  return PRESETS.includes(n as ReportRangeDays) ? (n as ReportRangeDays) : null;
}

export function useReportRange(defaultDays: ReportRangeDays = 30) {
  const [searchParams, setSearchParams] = useSearchParams();

  const range = useMemo((): ReportRange => {
    const from = searchParams.get('from');
    const to = searchParams.get('to');
    if (from && to) {
      const fromMs = new Date(`${from}T00:00:00`).getTime();
      const toMs = new Date(`${to}T00:00:00`).getTime();
      const days = Math.max(1, Math.round((toMs - fromMs) / 86400000) + 1);
      return {
        days,
        from,
        to,
        label: `${fmtShortDate(from)} – ${fmtShortDate(to)}`,
        isCustom: true,
      };
    }
    return rangeForPreset(parseDays(searchParams.get('days')) ?? defaultDays);
  }, [searchParams, defaultDays]);

  const setRange = useCallback(
    (next: ReportRange) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next.isCustom) {
            params.set('from', next.from);
            params.set('to', next.to);
            params.delete('days');
          } else {
            params.set('days', String(next.days));
            params.delete('from');
            params.delete('to');
          }
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setPreset = useCallback(
    (days: ReportRangeDays) => {
      setRange(rangeForPreset(days));
    },
    [setRange]
  );

  return { range, setRange, setPreset, presets: PRESETS, presetLabels: PRESET_LABEL };
}
